import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import bcrypt from 'bcryptjs';
import {
  Users, ChevronDown, ChevronRight, Eye, EyeOff,
  Check, X, Loader2, RefreshCw, UserPlus, AlertCircle, Link2,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

type RowStatus = 'idle' | 'creating' | 'done' | 'error';
type RowMode   = 'create' | 'link';

interface NegRow {
  id:         string;
  firstname:  string;
  lastname:   string;
  agencies:   string[];
  email:      string;
  tempPass:   string;
  mode:       RowMode;
  linkUserId: string;
  status:     RowStatus;
  errorMsg:   string;
}

interface AvailableUser {
  id:        string;
  firstname: string;
  lastname:  string;
  email:     string;
}

const CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
const genPassword = () =>
  Array.from({ length: 10 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join('');

// ─── Component ───────────────────────────────────────────────────────────────

export default function NegociateurSyncPanel({ onAccountCreated }: { onAccountCreated?: () => void }) {
  const [rows,           setRows]           = useState<NegRow[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [isOpen,         setIsOpen]         = useState(false);
  const [showPass,       setShowPass]       = useState<Set<string>>(new Set());
  const [creatingAll,    setCreatingAll]    = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: linked } = await supabase
        .from('users')
        .select('linked_negotiator_id')
        .not('linked_negotiator_id', 'is', null);

      const linkedIds = new Set((linked || []).map((u: any) => u.linked_negotiator_id));

      const { data: negs } = await supabase
        .from('negotiators')
        .select('id, firstname, lastname, negotiator_agencies(agency)')
        .eq('archived', false)
        .neq('firstname', 'DIRECTION')
        .order('lastname');

      const unlinked = (negs || []).filter((n: any) => !linkedIds.has(n.id));

      // Utilisateurs sans linked_negotiator_id
      const { data: users } = await supabase
        .from('users')
        .select('id, firstname, lastname, email')
        .is('linked_negotiator_id', null)
        .order('lastname');

      setAvailableUsers(users || []);

      setRows(prev => {
        const prevMap = new Map(prev.map(r => [r.id, r]));
        return unlinked.map((n: any) => {
          const existing = prevMap.get(n.id);
          return {
            id:         n.id,
            firstname:  n.firstname,
            lastname:   n.lastname,
            agencies:   (n.negotiator_agencies || []).map((na: any) => na.agency),
            email:      existing?.email      ?? '',
            tempPass:   existing?.tempPass   ?? genPassword(),
            mode:       existing?.mode       ?? 'link',
            linkUserId: existing?.linkUserId ?? '',
            status:     existing?.status     ?? 'idle',
            errorMsg:   existing?.errorMsg   ?? '',
          };
        });
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const updateRow = (id: string, patch: Partial<NegRow>) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));

  const togglePass = (id: string) =>
    setShowPass(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── Actions ──────────────────────────────────────────────────────────────

  const linkToUser = async (row: NegRow) => {
    if (!row.linkUserId) return;
    updateRow(row.id, { status: 'creating', errorMsg: '' });
    try {
      const { error } = await supabase
        .from('users')
        .update({ linked_negotiator_id: row.id, fonction: 'negociateur' })
        .eq('id', row.linkUserId);
      if (error) throw error;
      updateRow(row.id, { status: 'done' });
      onAccountCreated?.();
    } catch (err: any) {
      updateRow(row.id, { status: 'error', errorMsg: err.message });
    }
  };

  const createAccount = async (row: NegRow): Promise<boolean> => {
    if (!row.email.trim()) return false;
    updateRow(row.id, { status: 'creating', errorMsg: '' });
    try {
      const salt   = await bcrypt.genSalt(10);
      const hashed = await bcrypt.hash(row.tempPass, salt);

      const { error } = await supabase.from('users').insert([{
        email:                       row.email.trim().toLowerCase(),
        firstname:                   row.firstname,
        lastname:                    row.lastname,
        agency:                      row.agencies.join(', '),
        role:                        'user',
        fonction:                    'negociateur',
        linked_negotiator_id:        row.id,
        password:                    hashed,
        email_notifications_enabled: true,
        email_2fa_enabled:           false,
      }]);

      if (error) throw error;
      updateRow(row.id, { status: 'done' });
      onAccountCreated?.();
      return true;
    } catch (err: any) {
      updateRow(row.id, { status: 'error', errorMsg: err.message });
      return false;
    }
  };

  const createAll = async () => {
    const eligible = rows.filter(r => r.mode === 'create' && r.email.trim() && r.status !== 'done');
    if (!eligible.length) return;
    setCreatingAll(true);
    for (const row of eligible) await createAccount(row);
    setCreatingAll(false);
  };

  const pending   = rows.filter(r => r.status !== 'done');
  const withEmail = pending.filter(r => r.mode === 'create' && r.email.trim());
  const doneCount = rows.filter(r => r.status === 'done').length;

  const userLabel = (u: AvailableUser) => `${u.firstname} ${u.lastname} — ${u.email}`;

  if (loading) return null;

  if (rows.length === 0) {
    return (
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700 font-medium">
        <Check size={15} className="shrink-0" />
        Tous les négociateurs sont liés à un compte utilisateur.
      </div>
    );
  }

  return (
    <div className="border border-amber-200 rounded-2xl overflow-hidden shadow-sm">

      {/* ── Header ── */}
      <button
        type="button"
        onClick={() => setIsOpen(p => !p)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-amber-50 hover:bg-amber-100/70 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-amber-200/60 flex items-center justify-center shrink-0">
            <Users size={14} className="text-amber-700" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-amber-800">
              {pending.length} négociateur{pending.length !== 1 ? 's' : ''} sans liaison compte utilisateur
            </p>
            {doneCount > 0 && (
              <p className="text-xs text-amber-600">{doneCount} lien{doneCount > 1 ? 's' : ''} créé{doneCount > 1 ? 's' : ''} dans cette session</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={e => { e.stopPropagation(); loadData(); }}
            title="Rafraîchir" className="p-1.5 rounded-lg hover:bg-amber-200 transition-colors">
            <RefreshCw size={13} className="text-amber-600" />
          </button>
          {isOpen
            ? <ChevronDown size={15} className="text-amber-600" />
            : <ChevronRight size={15} className="text-amber-600" />
          }
        </div>
      </button>

      {/* ── Content ── */}
      {isOpen && (
        <>
          <div className="flex items-center gap-3 px-5 py-3 bg-slate-50 border-y border-slate-200">
            <div className="flex items-start gap-1.5 flex-1 text-xs text-slate-500">
              <AlertCircle size={13} className="shrink-0 mt-0.5 text-slate-400" />
              <span>
                <strong className="text-amber-700">Lier</strong> — sélectionnez un compte existant.
                <span className="mx-2 text-slate-300">|</span>
                <strong className="text-indigo-700">Créer</strong> — nouveau compte avec mot de passe temporaire.
              </span>
            </div>
            {withEmail.length > 0 && (
              <button type="button" onClick={createAll} disabled={creatingAll}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors shrink-0 whitespace-nowrap">
                {creatingAll
                  ? <><Loader2 size={12} className="animate-spin" /> Création…</>
                  : <><UserPlus size={12} /> Créer les {withEmail.length}</>
                }
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Négociateur</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Agences</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Mode</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[220px]">Compte / Email</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Mot de passe</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {rows.map(row => (
                  <tr key={row.id} className={`transition-colors ${
                    row.status === 'done'  ? 'bg-emerald-50/40' :
                    row.status === 'error' ? 'bg-red-50/30'     : 'hover:bg-slate-50/40'
                  }`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-semibold text-slate-800">{row.firstname} {row.lastname}</span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {row.agencies.length > 0
                          ? row.agencies.map(a => (
                              <span key={a} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs whitespace-nowrap">{a}</span>
                            ))
                          : <span className="text-xs text-slate-400 italic">Aucune</span>
                        }
                      </div>
                    </td>

                    {/* Mode toggle */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.status !== 'done' && (
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 w-fit">
                          <button type="button"
                            onClick={() => updateRow(row.id, { mode: 'link', status: 'idle', errorMsg: '' })}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-all ${
                              row.mode === 'link' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            }`}>
                            <Link2 size={10} /> Lier
                          </button>
                          <button type="button"
                            onClick={() => updateRow(row.id, { mode: 'create', status: 'idle', errorMsg: '' })}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-all ${
                              row.mode === 'create' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            }`}>
                            <UserPlus size={10} /> Créer
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Compte / Email */}
                    <td className="px-4 py-3">
                      {row.status === 'done' ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                          <Check size={12} /> {row.mode === 'link' ? 'Lié' : row.email}
                        </span>
                      ) : row.mode === 'link' ? (
                        <select value={row.linkUserId}
                          onChange={e => updateRow(row.id, { linkUserId: e.target.value, status: 'idle', errorMsg: '' })}
                          disabled={row.status === 'creating'}
                          className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 bg-white disabled:opacity-50">
                          <option value="">— Sélectionner un compte —</option>
                          {availableUsers.map(u => (
                            <option key={u.id} value={u.id}>{userLabel(u)}</option>
                          ))}
                        </select>
                      ) : (
                        <input type="email" value={row.email}
                          onChange={e => updateRow(row.id, { email: e.target.value, status: 'idle', errorMsg: '' })}
                          placeholder="prenom.nom@agence.fr"
                          disabled={row.status === 'creating'}
                          className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 disabled:opacity-50" />
                      )}
                    </td>

                    {/* Mot de passe */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.status !== 'done' && row.mode === 'create' && (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs text-slate-700 tracking-wide">
                            {showPass.has(row.id) ? row.tempPass : '••••••••••'}
                          </span>
                          <button type="button" onClick={() => togglePass(row.id)}
                            className="p-0.5 rounded text-slate-400 hover:text-slate-600 transition-colors">
                            {showPass.has(row.id) ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.status === 'creating' && <Loader2 size={15} className="animate-spin text-amber-500" />}

                      {row.status === 'done' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-semibold">
                          <Check size={11} /> Lié
                        </span>
                      )}

                      {row.status === 'error' && (
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1 text-xs text-red-600"><X size={11} /> Erreur</span>
                          <span className="text-[10px] text-red-500 max-w-[140px] truncate" title={row.errorMsg}>{row.errorMsg}</span>
                          <button type="button"
                            onClick={() => row.mode === 'link' ? linkToUser(row) : createAccount(row)}
                            className="mt-1 px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-200 transition-colors">
                            Réessayer
                          </button>
                        </div>
                      )}

                      {row.status === 'idle' && row.mode === 'link' && (
                        <button type="button" onClick={() => linkToUser(row)}
                          disabled={!row.linkUserId}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-xl text-xs font-semibold hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                          <Link2 size={12} /> Lier
                        </button>
                      )}

                      {row.status === 'idle' && row.mode === 'create' && (
                        <button type="button" onClick={() => createAccount(row)}
                          disabled={!row.email.trim()}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                          <UserPlus size={12} /> Créer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
